import { computed, WritableSignal } from '@angular/core';

import { EntityWithUuid, NkCardListData } from '@nk/ui-lib';
import { ClassMethodName, Dict } from '@nk/utils-lib';

/**
 * Represents the state of entities, including entities by UUID and additional metadata.
 */
export interface EntityState<Entity extends EntityWithUuid> extends NkCardListData<Entity> {
  entitiesByUuid: Dict<Entity>;
}

/**
 * Represents the loading state of various operations in the service.
 * Maps method names to their loading status (true if loading, false otherwise).
 * There can be several methods fetching data in the service.
 */
export type EntityLoadingState<T> = Partial<Record<ClassMethodName<T, keyof T>, boolean>>;

/**
 * Abstract base class for managing state and loading status of entities.
 *
 * This class provides reusable logic for managing entity state and loading indicators,
 * enabling derived classes to focus on specific entity-related operations.
 *
 * @template Entity - The type of entity managed by the state service.
 * @template ClassName - The class containing methods to track loading states.
 */
export abstract class AbstractEntityStateService<Entity extends EntityWithUuid, ClassName> {
  /**
   * Signal holding the current state of entities.
   * Should be initialized by the derived class with an appropriate default state.
   */
  protected entityState!: WritableSignal<EntityState<Entity>>;
  /**
   * Signal holding the current loading states for various operations.
   * Should be initialized by the derived class with an appropriate default state.
   */
  protected loadingState!: WritableSignal<EntityLoadingState<ClassName>>;

  /**
   * Computed signal to access the current entity state.
   */
  public state = computed(() => this.entityState());
  /**
   * Computed signal to determine if any operation is currently loading.
   */
  public isLoading = computed(() => Object.values(this.loadingState()).some((i) => i === true));

  /**
   * Updates the loading status for a specific operation.
   */
  protected updateLoadings(key: keyof EntityLoadingState<ClassName>, isLoading: boolean): void {
    this.loadingState.update((loadings) => {
      return { ...loadings, [key]: isLoading };
    });
  }

  /**
   * Updates the entity state using a provided update function.
   */
  protected updateState(updateFn: (state: EntityState<Entity>) => EntityState<Entity>): void {
    this.entityState.update(updateFn);
  }
}
